<template>
  <div
    v-b-popover.hover.topright="'Add audit period'"
    class="hover-pointer"
    @click="openModal"
  >
    {{ dataAudit.item.audit_days }}

    <b-modal
      v-model="showModal"
      :title="`Audit periods for ${dataAudit.item.user && dataAudit.item.user.name ? dataAudit.item.user.name : ''}`"
      centered
      size="lg"
      no-close-on-backdrop
      hide-footer
    >
      <div>
        <p class="text-muted mb-2">
          Add one or more audit periods for this payslip. Each period requires a start date, end date, role and type.
        </p>

        <!-- Existing periods preview -->
        <div
          v-if="loadingExisting"
          class="text-center my-1"
        >
          <b-spinner
            small
            type="grow"
            label="Loading..."
          />
        </div>

        <div
          v-else-if="existingPeriods.length"
          class="mb-2"
        >
          <h6 class="font-weight-bold mb-1">
            Existing periods
          </h6>
          <b-table
            :items="existingPeriods"
            :fields="existingFields"
            small
            bordered
            responsive
            show-empty
            empty-text="No existing periods"
          >
            <template #cell(actions)="data">
              <b-button
                variant="outline-danger"
                size="sm"
                @click="confirmDeletePeriod(data.item)"
              >
                Delete
              </b-button>
            </template>
          </b-table>
        </div>

        <!-- New periods form -->
        <div
          v-for="(period, index) in periods"
          :key="index"
          class="border rounded p-1 mb-1"
        >
          <b-row>
            <b-col
              cols="12"
              md="6"
              class="mb-1"
            >
              <label class="d-block font-weight-bold">Start date</label>
              <b-form-datepicker
                v-model="period.start_date"
                class="w-100"
                :max="period.end_date || null"
              />
            </b-col>

            <b-col
              cols="12"
              md="6"
              class="mb-1"
            >
              <label class="d-block font-weight-bold">End date</label>
              <b-form-datepicker
                v-model="period.end_date"
                class="w-100"
                :min="period.start_date || null"
              />
            </b-col>

            <b-col
              cols="12"
              md="6"
              class="mb-1"
            >
              <label class="d-block font-weight-bold">Role</label>
              <b-form-select
                v-model="period.role"
                :options="roleOptions"
                class="w-100"
              />
            </b-col>

            <b-col
              cols="12"
              md="6"
              class="mb-1"
            >
              <label class="d-block font-weight-bold">Type</label>
              <b-form-select
                v-model="period.type"
                :options="typeOptions"
                class="w-100"
              />
            </b-col>
          </b-row>

          <div class="d-flex justify-content-end">
            <b-button
              v-if="periods.length > 1"
              variant="outline-danger"
              size="sm"
              @click="removePeriod(index)"
            >
              Remove
            </b-button>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mt-1">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="addPeriod"
          >
            Add another period
          </b-button>

          <div>
            <b-button
              variant="secondary"
              class="mr-50"
              :disabled="saving"
              @click="showModal = false"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              :disabled="saving"
              @click="save"
            >
              {{ saving ? 'Saving...' : 'Save' }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'AddAuditPeriod',
  props: {
    dataAudit: {
      type: Object,
      required: true,
    },
    getAllDataPass: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showModal: false,
      saving: false,
      loadingExisting: false,
      existingPeriods: [],
      periods: [
        {
          start_date: null,
          end_date: null,
          role: null,
          type: null,
        },
      ],
      roleOptions: [
        { value: 'lead auditor', text: 'Lead auditor' },
        { value: 'auditor', text: 'Auditor' },
        { value: 'observer', text: 'Observer' },
      ],
      typeOptions: [
        { value: 'onsite', text: 'Onsite' },
        { value: 'overseas', text: 'Overseas' },
      ],
      existingFields: [
        { key: 'start_date', label: 'Start date' },
        { key: 'end_date', label: 'End date' },
        { key: 'role', label: 'Role' },
        { key: 'type', label: 'Type' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  methods: {
    openModal() {
      this.resetPeriods()
      this.fetchExistingPeriods()
      this.showModal = true
    },
    resetPeriods() {
      this.periods = [
        {
          start_date: null,
          end_date: null,
          role: null,
          type: null,
        },
      ]
    },
    addPeriod() {
      this.periods.push({
        start_date: null,
        end_date: null,
        role: null,
        type: null,
      })
    },
    removePeriod(index) {
      this.periods.splice(index, 1)
    },
    async fetchExistingPeriods() {
      this.loadingExisting = true
      this.existingPeriods = []
      try {
        const url = `/payslip/${this.dataAudit.item.id}/audit-periods`
        const response = await axios.get(url)
        const decrypted = decryptData(response.data.payload)?.data?.audit_periods

        let list = []
        if (decrypted && Array.isArray(decrypted.periods)) {
          list = decrypted.periods
        } else if (decrypted && decrypted.data && Array.isArray(decrypted.data.periods)) {
          list = decrypted.data.periods
        } else if (Array.isArray(decrypted)) {
          list = decrypted
        }

        this.existingPeriods = list.map(p => ({
          id: p.id,
          start_date: p.start_date,
          end_date: p.end_date,
          role: p.role,
          type: p.type,
        }))
      } catch (error) {
        // ignore, show empty state
        this.existingPeriods = []
      } finally {
        this.loadingExisting = false
      }
    },
    async confirmDeletePeriod(period) {
      const result = await this.$swal.fire({
        title: 'Delete period?',
        text: 'Are you sure you want to delete this audit period?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
      })

      if (!result.isConfirmed) return

      try {
        const url = `/payslip/${this.dataAudit.item.id}/audit-period/${period.id}`
        await axios.delete(url)
        this.$swal.fire('Deleted', 'Audit period deleted successfully.', 'success')
        await this.fetchExistingPeriods()
        this.getAllDataPass()
      } catch (error) {
        this.$swal.fire('Error', 'Failed to delete audit period.', 'error')
      }
    },
    async save() {
      const validPeriods = this.periods.filter(p => p.start_date && p.end_date && p.role && p.type)

      if (!validPeriods.length) {
        this.$swal.fire('Validation', 'Please fill at least one complete audit period.', 'warning')
        return
      }

      this.saving = true
      try {
        const payload = {
          periods: validPeriods.map(p => ({
            start_date: p.start_date,
            end_date: p.end_date,
            role: p.role,
            type: p.type,
          })),
        }
        const url = `/payslip/${this.dataAudit.item.id}/audit-periods`
        await axios.post(url, payload)
        this.$swal.fire('Success', 'Audit periods saved successfully.', 'success')
        this.showModal = false
        this.getAllDataPass()
      } catch (error) {
        let message = 'Failed to save audit periods.'
        if (error.response && error.response.data && error.response.data.payload) {
          try {
            const payload = decryptData(error.response.data.payload).message
            const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
            message = firstArrayKey ? payload[firstArrayKey][0] : message
          } catch (e) {
            // ignore decrypt errors and keep default message
          }
        }
        this.$swal.fire('Error', message, 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
